import * as G6 from '@antv/g6';
import { polygonContains, polygonHull } from 'd3-polygon';
import { throttle } from 'lodash/fp';

import Behavior from 'dashboard/components/GraphCanvasV2/behaviors/behavior';
import EventBus from 'dashboard/components/GraphCanvasV2/event-bus';
import CanvasMouseEnterEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-enter-event';
import CanvasMouseMoveEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-move-event';
import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import CollapsedGroupMouseEnterEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-group-mouse-enter-event';
import CollapsedGroupMouseLeaveEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-group-mouse-leave-event';
import CollapsedGroupPositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-group-position-change';
import CollapsedPairwiseNodeMouseEnterEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-pairwise-node-mouse-enter-event';
import CollapsedPairwiseNodeMouseLeaveEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-pairwise-node-mouse-leave-event';
import CollapsedPairwiseNodePositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/collapsed-pairwise-node-position-change';
import GraphEvent from 'dashboard/components/GraphCanvasV2/events/graph-event';
import HoverCardMouseEnterEvent from 'dashboard/components/GraphCanvasV2/events/hover-card-mouse-enter-event';
import HoverCardMouseLeaveEvent from 'dashboard/components/GraphCanvasV2/events/hover-card-mouse-leave-event';
import NodeMouseEnterEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-enter-event';
import NodeMouseLeaveEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-leave-event';
import NodePositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/node-position-change';
import RendererLayoutChangeEvent from 'dashboard/components/GraphCanvasV2/events/renderer-layout-change-event';
import SelectionBoxMouseEnterEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-mouse-enter-event';
import SelectionBoxMouseMoveEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-mouse-move-event';
import {
  g6IdToCollapsedGroupId,
  g6IdToCollapsedPairwiseNodeId,
  g6IdToNodeId,
} from 'dashboard/components/GraphCanvasV2/G6Mapper';
import G6Wrapper from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import {
  CanvasSelectable,
  collapsedGroupIdToSelectable,
  collapsedPairwiseNodeIdToSelectable,
  nodeIdToHashable,
  nodeIdToSelectable,
  RenderedCanvasNodeType,
} from 'dashboard/models/graph';
import { GraphExplorer } from 'dashboard/models/graph-explorer';

type Point = {
  readonly x: number;
  readonly y: number;
};

/**
 * A simple [Convex Hull](https://en.wikipedia.org/wiki/Convex_hull) for
 * tracking when the mouse is moving between a node and its related hover card(s).
 */
class Hull {
  readonly hull: Array<[number, number]>;

  constructor(polygonPoints: Point[]) {
    const points = polygonPoints.map((p) => [p.x, p.y]) as Array<[number, number]>;
    const hull = polygonHull(points);
    if (!hull) {
      // Null here means fewer than 3 points were provided (hence it's a degenerate polygon).
      throw new Error(`Hull could not be constructed for ${points}`);
    }
    this.hull = hull;
  }

  public contains(p: Point) {
    return polygonContains(this.hull, [p.x, p.y]);
  }
}

export default class HoverCardBehavior extends Behavior {
  private timeoutMillis = 200;
  private hoverCardTimeouts: Map<G6.INode, ReturnType<typeof setTimeout>>;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private activeG6Node: G6.INode;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private hoverCardHull: Hull;

  constructor(
    protected g6Wrapper: G6Wrapper,
    protected graphExplorer: GraphExplorer,
    protected eventBus: EventBus,
  ) {
    super(g6Wrapper, graphExplorer, eventBus);
    this.hoverCardTimeouts = new Map();
  }

  public get events() {
    return {
      [CanvasMouseEnterEvent.type]: 'handleCanvasMouseEnter',
      [CanvasMouseMoveEvent.type]: 'handleCanvasMouseMove',
      [CanvasViewportChangeEvent.type]: 'handleCanvasViewportChange',
      [RendererLayoutChangeEvent.type]: 'handleRendererLayoutChange',
      [NodeMouseEnterEvent.type]: 'handleNodeMouseEnter',
      [NodeMouseLeaveEvent.type]: 'handleNodeMouseLeave',
      [NodePositionChangeEvent.type]: 'handleNodePositionChange',
      [CollapsedGroupMouseEnterEvent.type]: 'handleCollapsedGroupMouseEnter',
      [CollapsedGroupMouseLeaveEvent.type]: 'handleCollapsedGroupMouseLeave',
      [CollapsedGroupPositionChangeEvent.type]: 'handleCollapsedGroupPositionChange',
      [CollapsedPairwiseNodeMouseEnterEvent.type]: 'handleCollapsedPairwiseNodeMouseEnter',
      [CollapsedPairwiseNodeMouseLeaveEvent.type]: 'handleCollapsedPairwiseNodeMouseLeave',
      [CollapsedPairwiseNodePositionChangeEvent.type]: 'handleCollapsedPairwiseNodePositionChange',
      [HoverCardMouseEnterEvent.type]: 'handleHoverCardMouseEnter',
      [HoverCardMouseLeaveEvent.type]: 'handleHoverCardMouseLeave',
      [SelectionBoxMouseEnterEvent.type]: 'handleSelectionBoxMouseEnter',
      [SelectionBoxMouseMoveEvent.type]: 'handleSelectionBoxMouseMove',
      // node mouse enter -> open context menu
      // node leave -> set timeout to close context menu
      // canvas enter -> clear timeout to close context menu if in the convex hull between node & menu
      // canvas exit -> set timeout to close context menu (arbitrary other objects in the hull signal the context menu to close)
      // context menu mouse enter -> clear timeout to close context menu
      // context menu mouse leave -> set timeout to close context menu
    };
  }

  public reactivate() {
    return;
  }

  public reset() {
    this.hoverCardTimeouts = new Map();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.hoverCardHull = null;
  }

  public handleCanvasMouseEnter(e: CanvasMouseEnterEvent) {
    this.resurrectHoverCardIfMouseInsideHull(e);
  }

  public handleCanvasMouseMove(e: CanvasMouseMoveEvent) {
    this.scheduleHoverCardClosureIfMouseOutsideHull(e);
  }

  public handleCanvasViewportChange(_e: CanvasViewportChangeEvent) {
    this.throttledClearHoverCardState();
  }

  public handleRendererLayoutChange(_e: RendererLayoutChangeEvent) {
    this.graphExplorer.closeHoverCard();
  }

  public handleNodeMouseEnter(e: NodeMouseEnterEvent) {
    const node = e.graphTarget;
    // Don't render context menu for hidden or dimmed nodes
    if (!node.state.visible || node.state.dimmed) {
      return;
    }
    this.activeG6Node = this.g6Wrapper.nodeByNodeId(node.id) as G6.INode;

    const timeoutId = this.hoverCardTimeouts.get(this.activeG6Node);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.hoverCardTimeouts.delete(this.activeG6Node);
    }

    this.throttledPositionHoverCard();
  }

  public handleCollapsedGroupMouseEnter(e: CollapsedGroupMouseEnterEvent) {
    const collapsedGroup = e.graphTarget;
    // Don't render context menu for hidden or dimmed nodes
    if (!collapsedGroup.state.visible || collapsedGroup.state.dimmed) {
      return;
    }
    this.activeG6Node = this.g6Wrapper.nodeByCollapsedGroupId(collapsedGroup.groupId) as G6.INode;

    const timeoutId = this.hoverCardTimeouts.get(this.activeG6Node);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.hoverCardTimeouts.delete(this.activeG6Node);
    }

    this.throttledPositionHoverCard();
  }

  public handleCollapsedPairwiseNodeMouseEnter(e: CollapsedPairwiseNodeMouseEnterEvent) {
    const collapsedNode = e.graphTarget;
    // Don't render context menu for hidden or dimmed nodes
    if (!collapsedNode.state.visible || collapsedNode.state.dimmed) {
      return;
    }
    this.activeG6Node = this.g6Wrapper.nodeByCollapsedPairwiseNodeId(
      collapsedNode.id.value,
    ) as G6.INode;

    const timeoutId = this.hoverCardTimeouts.get(this.activeG6Node);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.hoverCardTimeouts.delete(this.activeG6Node);
    }

    this.throttledPositionHoverCard();
  }

  public handleNodeMouseLeave(e: NodeMouseLeaveEvent) {
    if (!this.graphExplorer.isHoverCardOpen() || !this.activeG6Node || this.activeG6Node.destroyed)
      return;

    const node = e.graphTarget;
    const nodeBbox = this.activeG6Node.getBBox();
    const nodePolygon = [
      [nodeBbox.minX, nodeBbox.minY],
      [nodeBbox.maxX, nodeBbox.minY],
      [nodeBbox.maxX, nodeBbox.maxY],
      [nodeBbox.minX, nodeBbox.maxY],
    ].map(([x, y]) => {
      return this.g6Wrapper.getClientByPoint(x, y);
    });

    // Creates one big "hot zone" we can use to keep all associated context menus open.
    this.hoverCardHull = new Hull(
      nodePolygon.concat(
        [...node.hoverCards.values()].flatMap((menu) => {
          const bbox = menu.getBoundingClientRect();
          return [
            { x: bbox.left, y: bbox.top },
            { x: bbox.right, y: bbox.top },
            { x: bbox.right, y: bbox.bottom },
            { x: bbox.left, y: bbox.bottom },
          ];
        }),
      ),
    );

    this.hoverCardTimeouts.set(
      this.activeG6Node,
      setTimeout(() => this.closeHoverCard(this.activeG6Node), this.timeoutMillis),
    );
  }

  public handleCollapsedGroupMouseLeave(e: CollapsedGroupMouseLeaveEvent) {
    if (!this.graphExplorer.isHoverCardOpen() || !this.activeG6Node || this.activeG6Node.destroyed)
      return;

    const collapsedGroup = e.graphTarget;
    const collapsedGroupBbox = this.activeG6Node.getBBox();
    const nodePolygon = [
      [collapsedGroupBbox.minX, collapsedGroupBbox.minY],
      [collapsedGroupBbox.maxX, collapsedGroupBbox.minY],
      [collapsedGroupBbox.maxX, collapsedGroupBbox.maxY],
      [collapsedGroupBbox.minX, collapsedGroupBbox.maxY],
    ].map(([x, y]) => {
      return this.g6Wrapper.getClientByPoint(x, y);
    });

    // Creates one big "hot zone" we can use to keep all associated context menus open.
    this.hoverCardHull = new Hull(
      nodePolygon.concat(
        [...collapsedGroup.hoverCards.values()].flatMap((menu) => {
          const bbox = menu.getBoundingClientRect();
          return [
            { x: bbox.left, y: bbox.top },
            { x: bbox.right, y: bbox.top },
            { x: bbox.right, y: bbox.bottom },
            { x: bbox.left, y: bbox.bottom },
          ];
        }),
      ),
    );

    this.hoverCardTimeouts.set(
      this.activeG6Node,
      setTimeout(() => this.closeHoverCard(this.activeG6Node), this.timeoutMillis),
    );
  }

  public handleCollapsedPairwiseNodeMouseLeave(e: CollapsedPairwiseNodeMouseLeaveEvent) {
    if (!this.graphExplorer.isHoverCardOpen() || !this.activeG6Node || this.activeG6Node.destroyed)
      return;

    const collapsedNode = e.graphTarget;
    const collapsedNodeBbox = this.activeG6Node.getBBox();
    const nodePolygon = [
      [collapsedNodeBbox.minX, collapsedNodeBbox.minY],
      [collapsedNodeBbox.maxX, collapsedNodeBbox.minY],
      [collapsedNodeBbox.maxX, collapsedNodeBbox.maxY],
      [collapsedNodeBbox.minX, collapsedNodeBbox.maxY],
    ].map(([x, y]) => {
      return this.g6Wrapper.getClientByPoint(x, y);
    });

    // Creates one big "hot zone" we can use to keep all associated context menus open.
    this.hoverCardHull = new Hull(
      nodePolygon.concat(
        [...collapsedNode.hoverCards.values()].flatMap((menu) => {
          const bbox = menu.getBoundingClientRect();
          return [
            { x: bbox.left, y: bbox.top },
            { x: bbox.right, y: bbox.top },
            { x: bbox.right, y: bbox.bottom },
            { x: bbox.left, y: bbox.bottom },
          ];
        }),
      ),
    );

    this.hoverCardTimeouts.set(
      this.activeG6Node,
      setTimeout(() => this.closeHoverCard(this.activeG6Node), this.timeoutMillis),
    );
  }

  public handleNodePositionChange(e: NodePositionChangeEvent) {
    const { graphTarget: nodePositionChange } = e;

    const hoveredSelectable = this.graphExplorer.getHoveredSelectable();
    if (hoveredSelectable?.type !== RenderedCanvasNodeType.Node) {
      return;
    }

    const selectedNodeId = nodeIdToHashable(hoveredSelectable.selectable);
    for (const nodeId of Object.keys(nodePositionChange)) {
      if (nodeId === selectedNodeId) {
        this.throttledPositionHoverCard();
        break;
      }
    }
  }

  public handleCollapsedGroupPositionChange(e: CollapsedGroupPositionChangeEvent) {
    const { graphTarget: collapsedGroupPositionChange } = e;

    const hoveredSelectable = this.graphExplorer.getHoveredSelectable();
    if (hoveredSelectable?.type !== RenderedCanvasNodeType.CollapsedGroup) {
      return;
    }

    const selectedCollapsedGroupId = hoveredSelectable.selectable;
    for (const collapsedGroupId of Object.keys(collapsedGroupPositionChange)) {
      if (collapsedGroupId === selectedCollapsedGroupId) {
        this.throttledPositionHoverCard();
        break;
      }
    }
  }

  public handleCollapsedPairwiseNodePositionChange(e: CollapsedPairwiseNodePositionChangeEvent) {
    const { graphTarget: collapsedNodePositionChange } = e;

    const hoveredSelectable = this.graphExplorer.getHoveredSelectable();
    if (hoveredSelectable?.type !== RenderedCanvasNodeType.CollapsedPairwiseNode) {
      return;
    }

    const selectedCollapsedNodeId = hoveredSelectable.selectable;
    for (const collapsedNodeId of Object.keys(collapsedNodePositionChange)) {
      if (collapsedNodeId === selectedCollapsedNodeId) {
        this.throttledPositionHoverCard();
        break;
      }
    }
  }

  public handleHoverCardMouseEnter(_e: HoverCardMouseEnterEvent) {
    // This is essentially resurrectHoverCardIfMouseInsideHull, but we
    // don't need to explicitly check if the mouse is within the hull.
    const timeoutId = this.hoverCardTimeouts.get(this.activeG6Node);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.hoverCardTimeouts.delete(this.activeG6Node);
    }
  }

  public handleHoverCardMouseLeave(e: HoverCardMouseLeaveEvent) {
    this.scheduleHoverCardClosure(e);
  }

  public handleSelectionBoxMouseEnter(e: SelectionBoxMouseEnterEvent) {
    this.resurrectHoverCardIfMouseInsideHull(e);
  }

  public handleSelectionBoxMouseMove(e: SelectionBoxMouseMoveEvent) {
    this.scheduleHoverCardClosureIfMouseOutsideHull(e);
  }

  // If the mouse is still within the hot zone defined by the previously established
  // convex hull, cancel the active context menu's pending closure.
  private resurrectHoverCardIfMouseInsideHull(e: GraphEvent<MouseEvent>) {
    if (!this.graphExplorer.isHoverCardOpen() || !this.activeG6Node || this.activeG6Node.destroyed)
      return;

    const timeoutId = this.hoverCardTimeouts.get(this.activeG6Node);
    if (timeoutId && this.hoverCardHull?.contains({ x: e.raw.x, y: e.raw.y })) {
      clearTimeout(timeoutId);
      this.hoverCardTimeouts.delete(this.activeG6Node);
    }
  }

  private scheduleHoverCardClosure(_e: GraphEvent<MouseEvent>) {
    if (!this.graphExplorer.isHoverCardOpen() || !this.activeG6Node) return;

    this.hoverCardTimeouts.set(
      this.activeG6Node,
      setTimeout(() => this.closeHoverCard(this.activeG6Node), this.timeoutMillis),
    );
  }

  private scheduleHoverCardClosureIfMouseOutsideHull(e: GraphEvent<MouseEvent>) {
    if (!this.graphExplorer.isHoverCardOpen() || !this.activeG6Node || !this.hoverCardHull) return;

    if (!this.hoverCardHull.contains({ x: e.raw.x, y: e.raw.y })) {
      this.hoverCardTimeouts.set(
        this.activeG6Node,
        setTimeout(() => this.closeHoverCard(this.activeG6Node), this.timeoutMillis),
      );
    } else {
      // (Optional) If we want to support resuscitating a dying context menu, then:
      // clearTimeout(this.hoverCardTimeout);
      // this.hoverCardTimeout = null;
    }
  }

  private throttledClearHoverCardState = throttle(16, () => {
    this.graphExplorer.closeHoverCard();
  });

  // TODO: We can smooth out the positioning of the context menu once we move
  // take context menu positioning out of React. Throttle with 8ms
  // to avoid queueing a ton of work on the main thread while we're rendering.
  private throttledPositionHoverCard = throttle(8, this.positionHoverCard);

  private positionHoverCard() {
    if (!this.activeG6Node) {
      return;
    }

    const { id: g6Id } = this.activeG6Node.get('model');

    let selectable: CanvasSelectable;

    const nodeId = g6IdToNodeId(g6Id);
    if (nodeId) {
      selectable = nodeIdToSelectable(nodeId);
    }
    const collapsedGroupId = g6IdToCollapsedGroupId(g6Id);
    if (collapsedGroupId) {
      selectable = collapsedGroupIdToSelectable(collapsedGroupId);
    }
    const collapsedNodeId = g6IdToCollapsedPairwiseNodeId(g6Id);
    if (collapsedNodeId) {
      selectable = collapsedPairwiseNodeIdToSelectable(collapsedNodeId);
    }

    const { maxX, maxY } = this.activeG6Node.getBBox();
    const { x: canvasX, y: canvasY } = this.g6Wrapper.getCanvasByPoint(maxX, maxY);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.graphExplorer.setHoverCard(selectable, canvasX + 22, canvasY + 7);
  }

  private closeHoverCard(node: G6.INode) {
    // NOTE: This assumes all closings of context menus will be scheduled and not expected to be immediate.
    // The nuanced state sequencing is important to be sure you can move rapidly between different nodes
    // without accidentally having the new node's context menu be simultaneously closed when the old one
    // finally disappears.
    const timeoutId = this.hoverCardTimeouts.get(node);
    if (!timeoutId) {
      // It's already closed.
      return;
    }
    clearTimeout(timeoutId);

    if (!this.graphExplorer.isHoverCardOpen()) {
      return;
    }

    if (this.activeG6Node !== node) return;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.activeG6Node = undefined;
    this.graphExplorer.closeHoverCard();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.hoverCardHull = undefined;
  }
}
